import { FC, useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  useToast,
  Center,
  Spinner,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdDownload,
  MdDelete,
  MdRefresh,
  MdCancel,
} from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { PageHeading, Panel, Table, AnimatedCard, containerVariants, cardVariants } from '@bemap/ui';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
import { formatDate, openUrl } from '@bemap/utils';
import {
  getAllCertificates,
  getCertificatesByCourse,
  generateCertificate,
  cancelCertificate,
  deleteCertificate,
  downloadCertificate,
  getCompletionStats,
  CourseCertificate,
  CompletionStats,
} from '../../services/certificates-service';
import { getCourses, Course } from '../../services/university-service';

const CertificatesPage: FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [certificates, setCertificates] = useState<CourseCertificate[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [stats, setStats] = useState<CompletionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCertificate, setSelectedCertificate] = useState<CourseCertificate | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [certificatesRes, coursesRes, statsRes] = await Promise.all([
        getAllCertificates(),
        getCourses(),
        getCompletionStats(),
      ]);

      setCertificates(certificatesRes.message || []);
      setCourses(coursesRes.message || []);
      setStats(statsRes.message || null);
    } catch (error: any) {
      toast({
        title: t('general.error'),
        description: error.message || t('general.error_loading_data'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterByCourse = async () => {
    if (!selectedCourse) {
      await loadData();
      return;
    }

    setFilterLoading(true);
    try {
      const response = await getCertificatesByCourse(parseInt(selectedCourse));
      setCertificates(response.message || []);
    } catch (error: any) {
      toast({
        title: t('general.error'),
        description: error.message || t('certificates.error_filtering_certificates'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFilterLoading(false);
    }
  };

  const handleGenerateCertificate = async (certificate: CourseCertificate) => {
    setActionLoading(true);
    try {
      await generateCertificate(certificate.id);
      toast({
        title: t('general.success'),
        description: t('certificates.certificate_generated_successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await loadData();
    } catch (error: any) {
      toast({
        title: t('general.error'),
        description: error.message || t('certificates.error_generating_certificate'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelCertificate = async (certificate: CourseCertificate) => {
    setActionLoading(true);
    try {
      await cancelCertificate(certificate.id);
      toast({
        title: t('general.success'),
        description: t('certificates.certificate_cancelled_successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await loadData();
    } catch (error: any) {
      toast({
        title: t('general.error'),
        description: error.message || t('certificates.error_cancelling_certificate'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCertificate = async () => {
    if (!selectedCertificate) return;

    setActionLoading(true);
    try {
      await deleteCertificate(selectedCertificate.id);
      toast({
        title: t('general.success'),
        description: t('certificates.certificate_deleted_successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      await loadData();
    } catch (error: any) {
      toast({
        title: t('general.error'),
        description: error.message || t('certificates.error_deleting_certificate'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadCertificate = async (certificate: CourseCertificate) => {
    try {
      const downloadUrl = await downloadCertificate(certificate.id);
      openUrl(downloadUrl);
    } catch (error: any) {
      toast({
        title: t('general.error'),
        description: error.message || t('certificates.error_downloading_certificate'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting':
        return 'yellow';
      case 'generated':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const filteredCertificates = certificates.filter(certificate =>
    certificate.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    certificate.course?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    certificate.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      header: t('certificates.student'),
      accessorKey: 'user.name',
      cell: ({ row }: any) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">{row.original.user?.name}</Text>
          <Text fontSize="sm" color="gray.500">{row.original.user?.email}</Text>
        </VStack>
      ),
    },
    {
      header: t('certificates.course'),
      accessorKey: 'course.title',
      cell: ({ row }: any) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">{row.original.course?.title}</Text>
          <Text fontSize="sm" color="gray.500">{row.original.course?.instructor}</Text>
        </VStack>
      ),
    },
    {
      header: t('certificates.status'),
      accessorKey: 'status',
      cell: ({ row }: any) => (
        <Badge colorScheme={getStatusColor(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      header: t('certificates.grade'),
      accessorKey: 'grade',
      cell: ({ row }: any) => (
        <Text>{row.original.grade ? `${row.original.grade}%` : 'N/A'}</Text>
      ),
    },
    {
      header: t('certificates.completion'),
      accessorKey: 'completionPercentage',
      cell: ({ row }: any) => (
        <Text>{row.original.completionPercentage}%</Text>
      ),
    },
    {
      header: t('certificates.issue_date'),
      accessorKey: 'issueDate',
      cell: ({ row }: any) => (
        <Text>{formatDate(row.original.issueDate)}</Text>
      ),
    },
    {
      header: t('certificates.actions'),
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <HStack spacing={2}>
          {row.original.status === 'generated' && (
            <Tooltip label={t('certificates.download_certificate')}>
              <IconButton
                aria-label={t('certificates.download_certificate')}
                icon={<MdDownload />}
                size="sm"
                colorScheme="blue"
                onClick={() => handleDownloadCertificate(row.original)}
              />
            </Tooltip>
          )}
          {row.original.status === 'waiting' && (
            <Tooltip label={t('certificates.generate_certificate')}>
              <IconButton
                aria-label={t('certificates.generate_certificate')}
                icon={<MdRefresh />}
                size="sm"
                colorScheme="green"
                onClick={() => handleGenerateCertificate(row.original)}
                isLoading={actionLoading}
              />
            </Tooltip>
          )}
          {row.original.status !== 'cancelled' && (
            <Tooltip label={t('certificates.cancel_certificate')}>
              <IconButton
                aria-label={t('certificates.cancel_certificate')}
                icon={<MdCancel />}
                size="sm"
                colorScheme="orange"
                onClick={() => handleCancelCertificate(row.original)}
                isLoading={actionLoading}
              />
            </Tooltip>
          )}
          <Tooltip label={t('certificates.delete_certificate')}>
            <IconButton
              aria-label={t('certificates.delete_certificate')}
              icon={<MdDelete />}
              size="sm"
              colorScheme="red"
              onClick={() => {
                setSelectedCertificate(row.original);
                onOpen();
              }}
            />
          </Tooltip>
        </HStack>
      ),
    },
  ];

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      as={VStack}
      align="stretch"
      w="100%"
      gap={4}
    >
      <PageHeading title={t('certificates.title')} />
      {/* Statistics Cards */}
      {stats && (
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={6}>
          <AnimatedCard variants={cardVariants}>
            <Stat>
              <StatLabel>{t('certificates.total_enrollments')}</StatLabel>
              <StatNumber>{stats.totalEnrollments || 0}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                {t('certificates.all_course_enrollments')}
              </StatHelpText>
            </Stat>
          </AnimatedCard>
          <AnimatedCard variants={cardVariants}>
            <Stat>
              <StatLabel>{t('certificates.completed_courses')}</StatLabel>
              <StatNumber>{stats.completedCourses || 0}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                {t('certificates.hundred_percent_completion')}
              </StatHelpText>
            </Stat>
          </AnimatedCard>
          <AnimatedCard variants={cardVariants}>
            <Stat>
              <StatLabel>{t('certificates.certificates_issued')}</StatLabel>
              <StatNumber>{stats.certificatesIssued || 0}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                {t('certificates.generated_certificates')}
              </StatHelpText>
            </Stat>
          </AnimatedCard>
          <AnimatedCard variants={cardVariants}>
            <Stat>
              <StatLabel>{t('certificates.completion_rate')}</StatLabel>
              <StatNumber>{stats.completionRate?.toFixed(1) || 0}%</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                {t('certificates.success_rate')}
              </StatHelpText>
            </Stat>
          </AnimatedCard>
        </SimpleGrid>
      )}

      <VStack align="stretch">
        {/* Filters */}
        <Flex gap={4} wrap="wrap">
          {/* Search */}
          <InputGroup maxW="400px">
            <InputLeftElement pointerEvents="none">
              <MdSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder={t('certificates.search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Select
            placeholder={t('certificates.filter_by_course')}
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            maxW="300px"
          >
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </Select>
          <Button
            onClick={handleFilterByCourse}
            isLoading={filterLoading}
            colorScheme="blue"
          >
            {t('certificates.apply_filter')}
          </Button>
          <Button
            onClick={() => {
              setSelectedCourse('');
              loadData();
            }}
            variant="outline"
          >
            {t('certificates.clear_filter')}
          </Button>
        </Flex>

        {/* Table */}
        <Panel>
          <Table
            data={filteredCertificates}
            columns={columns}
            tableId="admin-certificates-table"
          />
        </Panel>
      </VStack>

      {/* Delete Confirmation Dialog */}
      <AlertDialog isOpen={isOpen} onClose={onClose} leastDestructiveRef={cancelRef}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('certificates.delete_confirmation_title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('certificates.delete_confirmation_message')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={onClose}>
                {t('certificates.cancel')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteCertificate} ml={3} isLoading={actionLoading}>
                {t('certificates.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </MotionBox>
  );
};

export default CertificatesPage; 